<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Contacts" backTo="internal">
      <button
          @click="addContactPanelOpen = true"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Create Contact</span>
      </button>
    </page-header>

    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="w-full flex items-center justify-between">
          <div class="flex -mx-0 mr-2">
            <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected" />
          </div>

          <div class="flex flex-col flex-1">
            <div class="flex justify-end">
              <input
                  type="text"
                  class="ml-6 w-full md:w-1/2 lg:w-1/3 xl:w-1/4 rounded-l bge-input bge-input-spacing"
                  placeholder="Search contacts by name, addresses or people"
                  v-model="searchTextInput"
                  @keydown.enter="triggerSearch"
              />
              <button
                  @click="triggerSearch"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
              >
                <Icon iconType="search" />
              </button>
            </div>
          </div>
        </div>

        <TabContent :currentTab="currentTab">
          <template v-slot:[currentTab]>
            <DataTableComponent
                :columns="contact_columns"
                @row-click="onRowClick"
                key="contacts_list"
                :rowClass="rowClass"
            >
              <template v-slot:contact-name-slot="{ rowData }">
                {{ formatDefaultContactName(rowData.people) }}
              </template>

              <template v-slot:telephone-slot="{ rowData }">
                {{ formatDefaultTelephone(rowData.people) }}
              </template>

              <template v-slot:postcode-slot="{ rowData }">
                {{ formatDefaultPostcode(rowData.addresses) }}
              </template>

              <template v-slot:city-slot="{ rowData }">
                {{ formatDefaultCity(rowData.addresses) }}
              </template>

              <template v-slot:type-badges-slot="{ rowData }">
                <div class="flex flex-wrap gap-2">
                  <span v-if="rowData.is_customer" class="tag tag-green">Customer</span>
                  <span v-if="rowData.is_distributor" class="tag tag-blue">Distributor</span>
                  <span v-if="rowData.is_supplier" class="tag tag-indigo">Supplier</span>
                  <span v-if="rowData.is_individual" class="tag tag-gray">Individual</span>
                </div>
              </template>

              <template v-slot:equipment-count-slot="{ rowData }">
                {{ formatNumber(rowData.equipmentCount) }}
              </template>
            </DataTableComponent>
          </template>
        </TabContent>
      </div>
    </div>

    <portal to="overlay-outlet"></portal>

    <portal to="overlay-outlet">
      <panel
          :showing="addContactPanelOpen"
          @close="addContactPanelOpen = false"
          title="Create New Contact"
      >
        <AddNewContact/>
      </panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import TabContent from "@/components/TabContent.vue";
import DataTableComponent from "@/views/Contacts/Components/DataTableComponent.vue";
import { mapActions, mapState } from "vuex";
import Icon from "@/components/Icon.vue";
import notificationMixin from "@/mixins/notificationMixin";
import AddNewContact from "@/components/Contacts/AddNew.vue";

export default {
  mixins: [notificationMixin],
  components: {
    AddNewContact,
    PageHeader,
    Spinner,
    Panel,
    TabHeaders,
    TabContent,
    DataTableComponent,
    Icon,
  },

  data() {
    return {
      addContactPanelOpen: false,
      tabs: [
        { id: "all", label: "All Contacts" },
        { id: "search", label: "Search" },
      ],

      contact_columns: [
        {
          field: "account_reference",
          header: "Customer Ref No",
          sortable: true,
          custom: false,
          width: "10%",
        },
        {
          field: "name",
          header: "Company Name",
          sortable: true,
          custom: false,
          width: "20%",
        },
        {
          field: "people",
          header: "Contact",
          sortable: false,
          custom: true,
          slotName: "contact-name-slot",
          width: "15%",
        },
        {
          field: "telephone",
          header: "Telephone",
          sortable: false,
          custom: true,
          slotName: "telephone-slot",
          width: "13%",
        },
        {
          field: "postcode",
          header: "Post Code",
          sortable: false,
          custom: true,
          slotName: "postcode-slot",
          width: "8%",
        },
        {
          field: "city",
          header: "City/Town",
          sortable: false,
          custom: true,
          slotName: "city-slot",
          width: "10%",
        },
        {
          field: "equipmentCount",
          header: "Equipment",
          sortable: true,
          custom: true,
          slotName: "equipment-count-slot",
          width: "8%",
        },
        {
          field: "type",
          header: "Type",
          sortable: false,
          custom: true,
          slotName: "type-badges-slot",
          width: "16%",
        },
      ],
    };
  },

  computed: {
    ...mapState("contact", ["currentTab", "searchText", "lastSelectedContact"]),

    searchTextInput: {
      get() {
        return this.searchText;
      },
      set(value) {
        this.$store.commit("contact/UPDATE_SEARCH_TEXT", value);
      },
    },
  },

  mounted() {
    this.fetchTableData();
  },

  methods: {
    ...mapActions("contact", ["fetchTableData", "updateTab", "resetCurrentPage"]),

    onTabSelected(newTab) {
      this.updateTab(newTab);
    },

    triggerSearch() {
      const term = (this.searchTextInput || "").trim();
      if (!term) return;

      this.resetCurrentPage(0);
      this.updateTab("search");
    },

    onRowClick(event) {
      this.$store.commit("contact/SET_LAST_SELECTED_CONTACT", event.data.contact_id);

      this.$router.push({
        name: "contacts-edit",
        params: { contactId: event.data.contact_id },
      });
    },

    rowClass(rowData) {
      const classes = [];

      if (rowData && rowData.contact_id && rowData.contact_id === this.lastSelectedContact) {
        classes.push("highlight-row");
      }

      return classes.join(" ");
    },

    getDefaultPerson(people) {
      if (!people || !people.length) return null;
      const def = people.find((p) => p && p.is_default === true);
      return def ? def : people[0];
    },

    getDefaultAddress(addresses) {
      if (!addresses || !addresses.length) return null;
      const def = addresses.find((a) => a && a.is_default === true);
      return def ? def : addresses[0];
    },

    formatDefaultContactName(people) {
      const p = this.getDefaultPerson(people);
      if (!p) return "";

      const first = (p.firstname || "").trim();
      const last = (p.lastname || "").trim();

      const full = [first, last].filter(Boolean).join(" ");
      return full || first || last || "";
    },

    formatDefaultTelephone(people) {
      const p = this.getDefaultPerson(people);
      if (!p) return "";
      if (p.telephone) return p.telephone;
      if (p.mobile) return p.mobile;
      return "";
    },

    formatDefaultPostcode(addresses) {
      const a = this.getDefaultAddress(addresses);
      if (!a) return "";
      return a.postcode ? a.postcode : "";
    },

    formatDefaultCity(addresses) {
      const a = this.getDefaultAddress(addresses);
      if (!a) return "";
      return a.city ? a.city : "";
    },

    formatNumber(v) {
      if (v === undefined || v === null) return "";
      const n = Number(v);
      if (Number.isNaN(n)) return "";
      return n.toLocaleString();
    },
  },
};
</script>

<style scoped>
::v-deep .highlight-row {
  background-color: rgba(0, 0, 0, 0.15) !important;
}

.tag {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  line-height: 1.25;
  font-weight: 600;
}

.tag-green {
  background: rgba(34, 197, 94, 0.18);
  border-color: rgba(34, 197, 94, 0.35);
  color: rgba(22, 101, 52, 1);
}

.tag-blue {
  background: rgba(59, 130, 246, 0.18);
  border-color: rgba(59, 130, 246, 0.35);
  color: rgba(30, 64, 175, 1);
}

.tag-indigo {
  background: rgba(99, 102, 241, 0.18);
  border-color: rgba(99, 102, 241, 0.35);
  color: rgba(49, 46, 129, 1);
}

.tag-gray {
  background: rgba(107, 114, 128, 0.18);
  border-color: rgba(107, 114, 128, 0.35);
  color: rgba(31, 41, 55, 1);
}
</style>
